<template>
  <div class="w-full h-full px-4 box-border flex flex-col overflow-hidden">
    <div class="w-full pt-1 pb-2 box-border flex items-center">
      <div class="flex items-center">
        <span class="text-medium text-theme-black-850 leading-22">标准分设置</span>
        <el-tooltip content="标准分指该维度默认合格分数，需小于“维度总分”或者“平均分”">
          <i class="iconfont icon-shuoming text-medium text-theme-black-350 cursor-pointer ml-0.5 mr-2"></i>
        </el-tooltip>
        <el-switch
          size="small"
          :disabled="statusLoading"
          v-model="topSource.standardValue"
          @change="setStatus"
        />
      </div>
      <div class="flex items-center ml-12">
        <span class="text-medium text-theme-black-850 leading-22">权重设置</span>
        <el-tooltip content="所有维度权重合计需为100%">
          <i class="iconfont icon-shuoming text-medium text-theme-black-350 cursor-pointer ml-0.5 mr-2"></i>
        </el-tooltip>
        <el-switch
          size="small"
          :disabled="statusLoading"
          v-model="topSource.weightValue"
          @change="setStatus"
        />
      </div>
    </div>
    <template v-if="columns.length > 0">
      <div
        class="w-full px-4 py-3 box-border text-medium text-theme-black-650 leading-none rounded-small bg-card-bg-gray"
      >
        标准分/权重设置开启后需全部填写，权重合计应为 100%
      </div>
      <div class="flex-1 mt-1 overflow-hidden">
        <data-table
          ref="dataTable"
          class="sms_table w-full h-full overflow-hidden"
          cell-class-name="truncate-table-cell-wrap"
          align="left"
          height="100%"
          :data="list"
          :loading="tableLoading"
          :columns="columns"
          :show-page="false"
          :show-tooltip="true"
          index
          v-if="showTable"
        >
          <template #standScore="{ row }">
            <el-input-number
              class="transform scale-90 origin-left"
              size="small"
              :min="0"
              :max="standScoreMax(row)"
              :precision="1"
              controls-position="right"
              v-model="row.standScore"
              @blur="blurCheckStandScore(row)"
            />
          </template>
          <template #weight="{ row }">
            <div class="transform scale-90 origin-left">
              <el-input-number
                size="small"
                :min="0"
                :max="100"
                :precision="0"
                controls-position="right"
                v-model="row.weight"
              />&nbsp;%
            </div>
          </template>
        </data-table>
      </div>
      <footer
        class="w-full px-4 pt-3 pb-4 box-border text-medium text-theme-black-650 leading-none text-right"
        v-if="topSource.weightValue"
      >
        当前权重合计：<span class="text-theme">{{totalWeight}}</span>&nbsp;%
      </footer>
    </template>
  </div>
</template>

<script>
import DataTable from '@/components/base/DataTable'
import {
  getStandardScoreWeightSettings,
  setStandardScoreWeightSettingStatus,
  saveStandardScoreWeight
} from '@/api/wenjuan/dimensionScore'

export default {
  name: 'StandardScoreWeightSetting',
  components: {
    DataTable
  },
  inject: {
    topSource: {
      default: () => ({})
    }
  },
  data() {
    return {
      list: [],
      tableLoading: false,
      columns: [],
      showTable: true,
      statusLoading: false,
    }
  },
  computed: {
    totalWeight() {
      let count = 0;
      for (const li of this.list) {
        count += (li.weight || 0);
      }
      return count;
    }
  },
  watch: {
    'topSource.standardValue': {
      handler() {
        this.showTable = false;
        this.setColumns();
        this.$nextTick(() => {
          this.showTable = true;
        });
      }
    },
    'topSource.weightValue': {
      handler() {
        this.showTable = false;
        this.setColumns();
        this.$nextTick(() => {
          this.showTable = true;
        });
      }
    },
  },
  mounted() {
    this.getList();
    this.setColumns();
    this.topSource.onNextBefore = this.checkStandardWeightScore.bind(this);
    this.topSource.onStepBefore = this.checkStandardWeightScore.bind(this);
  },
  beforeDestroy() {
    this.topSource.onNextBefore = null;
    this.topSource.onStepBefore = null;
  },
  methods: {
    standScoreMax(row) {
      return this.topSource.computeMode === 0 ? (row.totalScore || 0) : (row.avg || 0);
    },
    setColumns() {
      const baseColumns = [
        {
          label: '维度名称',
          value: 'name',
          width: '300px'
        }
      ];
      const standardColumns = [
        this.topSource.computeMode === 0 ?
        {
          label: '维度总分',
          value: 'totalScore',
          width: '120px'
        } : {
          label: '维度平均分',
          value: 'avg',
          width: '120px'
        },
        {
          label: '标准分',
          value: 'standScore',
          width: '135px'
        }
      ];
      const weightColumns = [
        {
          label: '权重',
          value: 'weight',
          width: '150px'
        }
      ];
      switch(true) {
        case !this.topSource.standardValue && !this.topSource.weightValue:
          this.columns = [];
          break;
        case this.topSource.standardValue && this.topSource.weightValue:
          this.columns = baseColumns.concat(standardColumns, weightColumns);
          break;
        case this.topSource.standardValue:
          this.columns = baseColumns.concat(standardColumns);
          break;
        case this.topSource.weightValue:
          baseColumns[0].width = '400px';
          this.columns = baseColumns.concat(weightColumns);
          break;
        default:
          this.columns = [];
      }
    },
    getList() {
      this.tableLoading = true;
      getStandardScoreWeightSettings()
        .then(res => {
          const list = res.data.data || [];
          this.list = list.map(m => {
            m.standScore = m.standScore ?? 0;
            m.weight = m.weight ?? 0;
            return m;
          });
        })
        .catch(() => {
          this.$message.error('获取标准分权重列表失败');
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    setStatus() {
      const { standardValue, weightValue } = this.topSource;
      const req = {
        standardScore: standardValue,
        weight: weightValue,
      };
      this.statusLoading = true;
      setStandardScoreWeightSettingStatus(req)
        .then(res => {
          const { code, msg } = res.data || {};
          if (code === 0) {
            this.$message.success('设置状态成功');
            if (standardValue || weightValue) {
              this.getList();
            }
          } else {
            this.$message.error(msg ?? '设置状态失败');
          }
        })
        .catch(() => {
          this.$message.error('设置状态失败');
        })
        .finally(() => {
          this.statusLoading = false;
        });
    },
    // 标准分失去焦点校验并校正是否大于平均分/总分
    blurCheckStandScore(row) {
      const score = this.topSource.computeMode === 0 ? (row.totalScore || 0) : (row.avg || 0);
      if (score < row.standScore) {
        row.standScore = score;
      }
    },
    // 验证权重
    checkWeight() {
      const isNotNull = this.list.every(e => !['', null, undefined].includes(e.weight));
      if (isNotNull) {
        let count = 0;
        for (const li of this.list) {
          count += (li.weight || 0);
        }
        const is100 = count === 100;
        if (!is100) {
          this.$message.warning('维度权重之和需为100%');
        }
        return is100;
      } else {
        this.$message.warning('请将剩余维度权重补充完整');
      }
      return false;
    },
    // 验证标准分
    checkStandardScore() {
      const isNotNull = this.list.every(e => !['', null, undefined].includes(e.standScore));
      if (isNotNull) return true;
      this.$message.warning('请将剩余维度标准分补充完整');
      return false;
    },
    // 验证标准分/权重
    checkStandardScoreWeight() {
      let count = 0;
      for (const li of this.list) {
        const weightNils = ['', null, undefined].includes(li.weight);
        const standardNils = ['', null, undefined].includes(li.standScore);
        if (weightNils || standardNils) {
          this.$message.warning('请将剩余维度标准分/权重补充完整');
          return false;
        }
        count += (li.weight || 0);
      }
      const is100 = count === 100;
      if (!is100) {
        this.$message.warning('维度权重之和需为100%');
      }
      return is100;
    },
    checkStandardWeightScore() {
      const { standardValue, weightValue } = this.topSource;
      if (!standardValue && !weightValue) return true;
      if (this.topSource.nextLoading) return false;
      let pass = false;
      switch(true) {
        case standardValue && weightValue:
          pass = this.checkStandardScoreWeight();
          break;
        case standardValue:
          pass = this.checkStandardScore();
          break;
        case weightValue:
          pass = this.checkWeight();
          break;
        default:
          pass = true;
      }
      // 提交数据
      if (pass) {
        return new Promise((resolve, reject) => {
          const req = Object.fromEntries(
            this.list.map(m => [m.name, { standardScore: m.standScore, weight: m.weight }])
          );
          this.topSource.nextLoading = true;
          saveStandardScoreWeight(req).then((res) => {
            const { code, msg } = res.data || {};
            if (code === 0) {
              this.$message.success('保存成功');
              resolve();
            } else {
              this.$message.success(msg ?? '保存失败');
              reject();
            }
          }).catch(() => {
            this.$message.success('保存失败');
            reject();
          }).finally(() => {
            this.topSource.nextLoading = false;
          });
        });
      } else {
        return false;
      }
    }
  }
}
</script>